import { Card } from '@cortex/web-design-system/components/ui/Card';
import { REVENUE_DEFINITIONS } from '../../lib/revenue-definitions.js';
import type { RevenueBlock, RevenueColumn } from '../../types/revenue.js';

/** @graph-connects none */
const BLOCK_ORDER: readonly RevenueBlock[] = ['売上', '前受金'];

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計の定義ビュー。ビジネス定義（会計士向けの収益認識基準）とエンジニア定義（ソースDB・テーブル・抽出ロジック）を横並びで一覧表示する。
 * @graph-connects REVENUE_DEFINITIONS [references, via:key] 指標定義データを参照する
 */
export function RevenueDefinitionView({ columns }: { columns: RevenueColumn[] }) {
  const grouped = BLOCK_ORDER.map((block) => ({
    block,
    metrics: columns.filter((col) => col.block === block),
  }));

  return (
    <div className="mt-6 space-y-6">
      {grouped.map(({ block, metrics }) => (
        <div key={block}>
          <h2 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">{block}</h2>
          <div className="flex flex-col gap-4">
            {metrics.map((col) => {
              const def = REVENUE_DEFINITIONS[col.key];
              return (
                <Card key={col.key} className="grid gap-6 overflow-hidden p-5 md:grid-cols-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-2 border-b border-[var(--color-border)] pb-3 md:col-span-2">
                    <span className="font-medium text-[var(--color-text-primary)]">
                      {col.label}
                    </span>
                    <span className="rounded-full bg-[var(--color-text-muted)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-text-secondary)]">
                      {def.type === 'stock' ? 'ストック' : 'フロー'}
                    </span>
                    <code className="ml-auto rounded bg-[var(--color-bg-subtle)] px-1.5 py-0.5 text-xs text-[var(--color-text-muted)]">
                      {col.key}
                    </code>
                  </div>
                  <div>
                    <div className="mb-2 text-[0.625rem] font-semibold uppercase tracking-widest text-[var(--color-text-muted)]">
                      ビジネス定義
                    </div>
                    <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">
                      {def.businessDescription}
                    </p>
                    {def.validation && (
                      <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                        <span className="font-medium">検証ルール:</span> {def.validation}
                      </p>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="mb-2 text-[0.625rem] font-semibold uppercase tracking-widest text-[var(--color-text-muted)]">
                      エンジニア定義
                    </div>
                    <pre className="whitespace-pre-wrap break-words rounded-md bg-[var(--color-bg-subtle)] px-3 py-2 font-mono text-xs leading-relaxed text-[var(--color-text-primary)]">
                      {def.technicalSource.logic}
                    </pre>
                    <p className="mt-2 whitespace-pre-line text-xs text-[var(--color-text-secondary)]">
                      {`ソース: ${def.technicalSource.database}${def.technicalSource.tables.length > 0 ? `\nテーブル: ${def.technicalSource.tables.join(', ')}` : ''}`}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
